import type { FC, ReactNode } from 'react'
import type { DeviceStatus } from '@/types'

import { useEffect, useRef } from 'react'
import { animate } from 'motion/react'
import { cn } from '@/lib/utils'

import { STATUS_META } from '@/constants/inventory'

export const glass = 'rounded-2xl border border-white/10 bg-[#070b14]/70 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.9)] backdrop-blur-xl'

interface StatusDotProps {
    status: DeviceStatus
    className?: string
}

export const StatusDot: FC<StatusDotProps> = ({ status, className }): ReactNode => {
    const color = STATUS_META[status].color
    const alarming = status === 'critical' || status === 'warning'

    return (
        <span className={cn('relative inline-flex size-2 shrink-0', className)} aria-hidden='true'>
            {alarming && <span className='absolute inset-0 animate-ping rounded-full opacity-70' style={{ backgroundColor: color }} />}
            <span className='relative inline-flex size-2 rounded-full' style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
        </span>
    )
}

interface CountUpProps {
    value: number
    format?: (value: number) => string
    duration?: number
    className?: string
}

// Counts up from zero the first time it appears. Only mounted on the client after
// the visitor has entered the hall, so the server output never differs.
export const CountUp: FC<CountUpProps> = ({ value, format = v => Math.round(v).toLocaleString('en-US'), duration = 1.4, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const formatRef = useRef(format)

    formatRef.current = format

    useEffect(() => {
        const node = ref.current

        if (!node) return

        const controls = animate(0, value, {
            duration,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: latest => {
                node.textContent = formatRef.current(latest)
            }
        })

        return () => controls.stop()
    }, [value, duration])

    return (
        <span ref={ref} className={className}>
            {format(value)}
        </span>
    )
}

interface MeterProps {
    value: number
    max: number
    color: string
    className?: string
}

export const Meter: FC<MeterProps> = ({ value, max, color, className }): ReactNode => {
    const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0

    return (
        <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-white/10', className)}>
            <div
                className='h-full rounded-full transition-[width] duration-700 ease-out'
                style={{ width: `${ratio * 100}%`, background: `linear-gradient(90deg, ${color}99, ${color})`, boxShadow: `0 0 12px ${color}66` }}
            />
        </div>
    )
}

interface KbdProps {
    children: ReactNode
    className?: string
}

export const Kbd: FC<KbdProps> = ({ children, className }): ReactNode => {
    return (
        <kbd className={cn('inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-white/15 bg-white/5 px-1.5 font-mono text-[10px] font-medium text-slate-400', className)}>
            {children}
        </kbd>
    )
}

export const LogoMark: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 32 32' fill='none' className={className} aria-hidden='true'>
            <rect x='5' y='3' width='22' height='26' rx='4' stroke='currentColor' strokeWidth='2' />
            <path d='M10 9.5h12M10 14h12M10 18.5h12M10 23h7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
            <circle cx='22' cy='23' r='1.8' fill='#34d399' />
        </svg>
    )
}
